"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var ReactDOM = require("react-dom");
var tabbable = require("tabbable");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var DomWidgets_1 = require("../DomWidgets");
var DialogBody_1 = require("./DialogBody");
var DialogFooter_1 = require("./DialogFooter");
var DialogHeader_1 = require("./DialogHeader");
var DialogWidget_1 = require("./DialogWidget");
var DialogContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    outline: 'none',
    overflow: 'hidden',
};
var DialogContent = /** @class */ (function (_super) {
    tslib_1.__extends(DialogContent, _super);
    function DialogContent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.sawESCkeyDown = false; // only handle ESC keyUp when dialog previously got ESC keyDown
        _this.handleDialogHeaderRef = function (header) {
            _this.dialogHeader = ReactDOM.findDOMNode(header);
        };
        _this.handleDialogKeyUp = function (e) {
            if (e.key === 'Escape' && _this.sawESCkeyDown) {
                _this.sawESCkeyDown = false;
                _this.props.onCloseRequest();
            }
        };
        _this.handleDialogKeyDown = function (e) {
            if (e.key === 'Escape') {
                // eat Escape keyDown since we'll handle it in keyUp
                _this.sawESCkeyDown = true;
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            else if (e.key === 'Tab') {
                // trap focus in the dialog
                var tabbables = tabbable(_this.dialog);
                var first = tabbables.length > 0 ? tabbables[0] : null;
                var last = tabbables.length > 0 ? tabbables[tabbables.length - 1] : null;
                if (e.target === last && !e.shiftKey) {
                    e.preventDefault();
                    first.focus();
                }
                else if (e.target === first && e.shiftKey) {
                    e.preventDefault();
                    last.focus();
                }
            }
        };
        return _this;
    }
    DialogContent.prototype.componentDidMount = function () {
        // Initially set the element to return focus to as the last active element.
        this.elementToReturnFocus = document.activeElement;
        this.dialog = ReactDOM.findDOMNode(this);
        if (!this.props.disableAutoFocus) {
            this.initializeFocusOnMount();
        }
        this.setFocusEventListener();
        document.addEventListener('focus', this.focusEventListener, true);
        // IE11 column-based flex containers need an explicit height to size flex children correctly. The symptom that we want to avoid
        // is when the content overflows the floater rather than shrinking, so apply this hack only if that happens.
        if (this.dialog.offsetHeight > this.dialog.parentElement.clientHeight) {
            this.dialog.style.height = this.dialog.parentElement.clientHeight + 'px';
        }
    };
    /**
     * It should focus on the first focusable element within the dialog content, otherwise focus on the first thing in the header
     * If no elements are focusable within the dialog it should throw an error.
     */
    DialogContent.prototype.initializeFocusOnMount = function () {
        var tabbables = tabbable(this.dialog);
        if (tabbables.length === 0) {
            throw Error('Something within the dialog must be focusable as to dismiss it');
        }
        // The first focusable element after the header if any elements exist or the first focusable element of the header if none do
        var idxOfElementToFocus = 0;
        if (!!this.dialogHeader) {
            for (var idx = 0; idx < tabbables.length; idx++) {
                if (!(this.dialogHeader.contains(tabbables[idx]))) {
                    idxOfElementToFocus = idx;
                    break;
                }
            }
        }
        var elementToFocus = tabbables[idxOfElementToFocus];
        // Queue this so that it occurs after the floater or overlay is positioned,
        // so that when the browser scrolls to the focused element it will scroll to the correct position.
        DomUtil_1.timeoutFocus(elementToFocus);
    };
    /**
     * If the dialog is nonModal it should keep track of the last element focused outside the dialog to be focused so when the dialog is
     * closed the focus can return to the last focused element.
     */
    DialogContent.prototype.setFocusEventListener = function () {
        var _this = this;
        this.focusEventListener = function (event) {
            if (!_this.props.isModal) {
                var target = event.target;
                if (!_this.dialog.contains(target)) {
                    _this.elementToReturnFocus = target;
                }
            }
        };
    };
    DialogContent.prototype.componentWillUnmount = function () {
        document.removeEventListener('focus', this.focusEventListener, true);
        if (this.props.shouldReturnFocusOnClose) {
            this.returnFocusToElement();
        }
    };
    /**
     * We need to return the focus to either the initial focused element if modal, or the last focused
     * element if nonModal.  We also need to dispatch the focus event so that it fires its onFocus
     * handlers.
     */
    DialogContent.prototype.returnFocusToElement = function () {
        /**
         * We must use setTimeout here because it is likely a focus event is causing the dialog to unmount.  We need
         * to queue this event so that it is called correctly.
         */
        DomUtil_1.timeoutFocus(this.elementToReturnFocus);
    };
    DialogContent.prototype.render = function () {
        var headerProps = tslib_1.__assign(tslib_1.__assign({}, this.props), { ref: this.handleDialogHeaderRef });
        var hasHeader = this.props.type !== DialogWidget_1.DialogType.Uber;
        return (React.createElement(DomWidgets_1.Block, { "aria-label": Declarative_1.conditional(!hasHeader, this.props.title), "aria-labelledby": Declarative_1.conditional(hasHeader && !!this.props.title, this.props.id), "aria-modal": this.props.isModal, "data-tb-test-id": Declarative_1.testId(this.props.testId, 'Content'), onKeyDown: this.handleDialogKeyDown, onKeyUp: this.handleDialogKeyUp, role: 'dialog', style: tslib_1.__assign(tslib_1.__assign({}, DialogContentStyles), this.props.dialogContentStyle), tabIndex: -1 },
            hasHeader && React.createElement(DialogHeader_1.DialogHeader, tslib_1.__assign({}, headerProps)),
            React.createElement(DialogBody_1.DialogBody, { children: this.props.children, dialogBodyStyle: this.props.dialogBodyStyle, showFooter: this.props.showFooter, testId: this.props.testId, type: this.props.type }),
            this.props.showFooter &&
                React.createElement(DialogFooter_1.DialogFooter, { children: this.props.footerContent, sidePadding: DialogBody_1.getBodyPaddingStyle(this.props.type, this.props.showFooter).paddingRight, testId: this.props.testId })));
    };
    return DialogContent;
}(React.Component));
exports.DialogContent = DialogContent;
//# sourceMappingURL=DialogContent.js.map